import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getList } from '../firebase/database';
import ListMetaTags from '../components/ListMetaTags';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTranslation } from '../hooks/useTranslation';
import { Gift, Users, Calendar, ArrowRight } from 'lucide-react';

const PublicListPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchList = async () => {
      try {
        const result = await getList(id);
        if (result.success && result.data) {
          setList(result.data);
        } else {
          setList(null);
        }
      } catch (error) {
        console.error('Error fetching list:', error);
        setList(null);
      } finally {
        setLoading(false);
      }
    };

    fetchList();
  }, [id]);

  // Redirect authenticated users to the full view
  useEffect(() => {
    if (user && list) {
      // Use replace to avoid back button issues
      navigate(`/list/${id}/view`, { replace: true });
    }
  }, [user, list, id, navigate]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!list) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('publicPreview.listNotFound')}</h1>
          <p className="text-gray-600">{t('publicPreview.listNotFoundDescription')}</p>
        </div>
      </div>
    );
  }

  const wishCount = list.wishCount || 0;
  const memberCount = list.members?.length || 1;
  const isGroupList = list.isGroup;

  return (
    <div className="min-h-screen bg-gray-50">
      <ListMetaTags list={list} />
      
      <div className="max-w-4xl mx-auto px-4 py-8 pt-20">
        <div className="text-center">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src="/ynskje large logo.png" 
              alt="Ynskje" 
              className="h-16 w-16 object-contain"
            />
          </div>
          
          {/* List Title */}
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{list.name}</h1>
          
          {/* List Description */}
          {list.description && (
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">{list.description}</p>
          )}
          
          {/* List Stats */}
          <div className="flex justify-center gap-6 mb-8">
            <div className="flex items-center text-gray-600">
              <Gift className="h-5 w-5 mr-2" />
              <span>{wishCount} {t('dashboard.wishes')}</span>
            </div>
            <div className="flex items-center text-gray-600">
              <Users className="h-5 w-5 mr-2" />
              <span>{memberCount} {t('dashboard.members')}</span>
            </div>
            {list.occasion && (
              <div className="flex items-center text-gray-600">
                <Calendar className="h-5 w-5 mr-2" />
                <span>{t(`createList.occasions.${list.occasion}`)}</span>
              </div>
            )}
          </div>
          
          {/* Preview Card */}
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {isGroupList ? t('publicPreview.joinGroupList') : t('publicPreview.viewWishList')}
            </h2>
            <p className="text-gray-600 mb-6">
              {isGroupList 
                ? t('publicPreview.groupListDescription')
                : t('publicPreview.personalListDescription')
              }
            </p>
            
            {/* Action Buttons */}
            <div className="space-y-3">
            <button
              onClick={() => navigate(`/list/${id}/view`, { state: { from: { pathname: `/list/${id}` } } })}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center"
            >
              <ArrowRight className="h-5 w-5 mr-2" />
              {isGroupList ? t('publicPreview.joinList') : t('publicPreview.viewList')}
            </button>
            
            <div className="flex space-x-2">
              <button
                onClick={() => navigate('/login', { state: { from: { pathname: `/list/${id}` } } })}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                {t('navigation.login')}
              </button>
              <button
                onClick={() => navigate('/register', { state: { from: { pathname: `/list/${id}` } } })}
                className="flex-1 bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                {t('navigation.register')}
              </button>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicListPreview;
